import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../contexts/AppContext";
import {
  LogOut, Settings, Package, Gift, Heart, User, Mail, Calendar,
  ShoppingBag, ChevronRight,
} from "lucide-react";
import { listOrdersByUser } from "../services/analytics";

export default function ProfilePage() {
  const { user, profile, logout, isAdmin, authChecked } = useAuth();
  const { t, lang } = useApp();
  const [orderCount, setOrderCount] = useState(null);

  // Locale-aware title-case helper — converts pre-uppercased translations
  // like "SEÇİLMİŞLƏR" / "SİFARİŞLƏRİM" to "Seçilmişlər" / "Sifarişlərim".
  const locale = lang === "ru" ? "ru-RU" : lang === "en" ? "en-US" : "az-AZ";
  const titleCase = (s) => {
    if (s == null) return "";
    const lower = String(s).toLocaleLowerCase(locale);
    return lower.replace(/(^|\s|-|\/)\S/g, (m) => m.toLocaleUpperCase(locale));
  };

  useEffect(() => {
    if (!user?.uid) return;
    listOrdersByUser(user.uid)
      .then((list) => setOrderCount(list.length))
      .catch(() => setOrderCount(0));
  }, [user?.uid]);

  if (!authChecked) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[var(--text-2)]">
        {t.common.loading}
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const displayName = profile?.full_name || user.email?.split("@")[0] || "—";
  const initials = (profile?.full_name || user.email || "?")
    .split(/[\s@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  const memberSinceDate = profile?.created_at?.toDate?.()
    ? profile.created_at.toDate()
    : null;
  const memberSinceText = memberSinceDate
    ? memberSinceDate.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
      })
    : "—";

  const cards = [
    {
      to: "/orders",
      icon: Package,
      title: titleCase(t.profile.myOrders),
      desc: t.profile.myOrdersDesc,
      badge: orderCount != null ? String(orderCount) : null,
      testid: "profile-orders",
    },
    {
      to: "/favorites",
      icon: Heart,
      title: titleCase(t.fav.title || "Seçilmişlər"),
      desc:
        t.profile.myFavoritesDesc ||
        "Bəyəndiyiniz vinillər və qorunub saxlanılan kolleksiyalar",
      testid: "profile-favorites",
    },
    {
      to: "/my-gift-cards",
      icon: Gift,
      title: titleCase(t.nav.myGiftCards || "Hədiyyə Kartlarım"),
      desc: t.profile.myGiftCardsDesc,
      testid: "profile-gift-cards",
    },
  ];

  return (
    <div className="bg-[var(--surface)] min-h-[calc(100vh-200px)]" data-testid="profile-page">
      <div className="max-w-5xl mx-auto px-5 md:px-12 py-12 md:py-20">

        {/* ── Profile header card ───────────────────────────────── */}
        <div className="bg-white border border-black/8 rounded-2xl p-7 md:p-10 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            {/* Avatar / initials */}
            <div className="flex-shrink-0">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-[var(--accent)] to-[var(--primary)] flex items-center justify-center text-white text-2xl md:text-3xl font-medium tracking-tight shadow-md">
                {initials || <User size={32} />}
              </div>
            </div>

            {/* Identity */}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.22em] uppercase text-black/40 font-medium mb-2">
                {titleCase(t.common.account)}
              </div>
              <h1 className="text-3xl md:text-4xl font-medium tracking-tight text-[var(--text)] leading-tight">
                {displayName}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-black/55">
                <span className="flex items-center gap-1.5">
                  <Mail size={14} className="opacity-70" />
                  <span className="truncate">{user.email}</span>
                </span>
                {memberSinceDate && (
                  <span className="flex items-center gap-1.5">
                    <Calendar size={14} className="opacity-70" />
                    <span>{memberSinceText}</span>
                  </span>
                )}
                {isAdmin && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[var(--accent)]/8 text-[var(--accent)] text-[11px] font-medium tracking-wide">
                    <Settings size={11} /> Admin
                  </span>
                )}
              </div>
            </div>

            {/* Right CTA — logout */}
            <div className="flex-shrink-0">
              <button
                onClick={logout}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-black/15 text-sm text-black/70 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
                data-testid="profile-logout"
                type="button"
              >
                <LogOut size={14} />
                <span>{titleCase(t.common.logout)}</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── Quick stats row ───────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
          <StatCard
            icon={ShoppingBag}
            label={titleCase(t.profile.myOrders)}
            value={orderCount != null ? orderCount : "—"}
          />
          <StatCard
            icon={Heart}
            label={titleCase(t.fav.title || "Seçilmişlər")}
            value={profile?.favorites_count ?? "—"}
          />
          <StatCard
            icon={Gift}
            label={titleCase(t.nav.myGiftCards || "Hədiyyə Kartları")}
            value={profile?.gift_cards_count ?? "—"}
          />
        </div>

        {/* ── Action cards ──────────────────────────────────────── */}
        <div className="mt-8">
          <div className="text-[10px] tracking-[0.22em] uppercase text-black/45 font-medium mb-3 px-1">
            Hesab bölmələri
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {cards.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                data-testid={c.testid}
                className="group bg-white border border-black/8 rounded-xl p-6 hover:border-[var(--accent)]/60 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="w-10 h-10 rounded-full bg-[var(--accent)]/8 flex items-center justify-center text-[var(--accent)]">
                    <c.icon size={17} strokeWidth={1.7} />
                  </span>
                  <ChevronRight
                    size={16}
                    className="text-black/30 group-hover:text-[var(--accent)] group-hover:translate-x-0.5 transition-all"
                  />
                </div>
                <div className="flex items-baseline gap-2">
                  <div className="text-base font-medium text-[var(--text)] leading-snug">
                    {c.title}
                  </div>
                  {c.badge != null && (
                    <span className="text-[11px] font-medium text-black/40">
                      ({c.badge})
                    </span>
                  )}
                </div>
                <div className="text-sm text-black/55 mt-1.5 leading-relaxed">
                  {c.desc}
                </div>
              </Link>
            ))}

            {isAdmin && (
              <Link
                to="/admin"
                data-testid="profile-admin"
                className="md:col-span-3 group bg-[var(--primary)] text-white rounded-xl p-6 hover:bg-[var(--primary-hover)] transition-all flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <Settings size={17} strokeWidth={1.7} />
                  </span>
                  <div>
                    <div className="text-base font-medium">{t.nav.admin}</div>
                    <div className="text-sm text-white/65 mt-0.5">
                      {t.profile.adminDesc}
                    </div>
                  </div>
                </div>
                <ChevronRight
                  size={18}
                  className="text-white/70 group-hover:translate-x-0.5 transition-transform"
                />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-black/8 rounded-xl px-5 py-4 flex items-center gap-4">
      <span className="w-9 h-9 rounded-full bg-black/4 flex items-center justify-center text-black/55">
        <Icon size={15} strokeWidth={1.7} />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.2em] uppercase text-black/40 font-medium">
          {label}
        </div>
        <div className="text-xl font-medium text-[var(--text)] mt-0.5 leading-none">
          {value}
        </div>
      </div>
    </div>
  );
}
